<template>
  <ScrollTop
    :target="target"
    :threshold="threshold"
    :icon="icon"
    :behavior="behavior"
    :class="[
      className,
      `p-scrolltop-${size}`,
      {
        'p-scrolltop-responsive': responsive,
        'p-scrolltop-rtl': dir === 'rtl'
      }
    ]"
    :style="style"
    :role="'button'"
    :aria-label="ariaLabel"
    :tabindex="0"
    @click="onClick"
    @keydown.enter="onClick"
  >
    <slot>
      <span :class="icon" :aria-hidden="true"></span>
    </slot>
  </ScrollTop>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ScrollTop from 'primevue/scrolltop'
import type { ONEScrollTopProps } from './types'

export default defineComponent({
  name: 'ONEScrollTop',
  components: {
    ScrollTop
  },
  inheritAttrs: false,
  emits: ['click']
})
</script>

<script setup lang="ts">
withDefaults(defineProps<ONEScrollTopProps>(), {
  target: 'window',
  threshold: 400,
  icon: 'pi pi-chevron-up',
  behavior: 'smooth',
  className: undefined,
  style: undefined,
  ariaLabel: 'Scroll to top',
  responsive: true,
  dir: 'ltr',
  size: 'normal'
})

const emit = defineEmits<{
  (e: 'click', event: Event): void
}>()

const onClick = (event: Event) => {
  emit('click', event)
}
</script>

<style lang="scss">
.p-scrolltop {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(33, 150, 243, 0.25);
  }

  &-small {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1rem;
  }

  &-normal {
    width: 3rem;
    height: 3rem;
    font-size: 1.25rem;
  }

  &-large {
    width: 3.5rem;
    height: 3.5rem;
    font-size: 1.5rem;
  }

  &-responsive {
    @media screen and (max-width: 576px) {
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1rem;
    }
  }

  &-rtl {
    direction: rtl;
    .pi {
      transform: rotate(180deg);
    }
  }

  // Theme integration
  background: var(--primary-color, #2196F3);
  color: var(--primary-color-text, #ffffff);

  &:hover {
    background: var(--primary-darker-color, #1976D2);
  }

  // Accessibility - High contrast mode
  @media screen and (-ms-high-contrast: active) {
    border: 2px solid currentColor;
  }

  // Animation
  &.p-scrolltop-enter-active,
  &.p-scrolltop-leave-active {
    transition: opacity 0.15s;
  }

  &.p-scrolltop-enter-from,
  &.p-scrolltop-leave-to {
    opacity: 0;
  }
}
</style>
